import React, { useCallback, useMemo } from 'react'
import { Box, Text, IconButton, useDisclosure, HStack, Tooltip } from '@chakra-ui/react'
import { FiPlus, FiTrash2, FiUsers } from 'react-icons/fi'
import { useAuth } from '@/contexts/AuthContext'
import { useAppTranslation } from '@/contexts/LanguageContext'
import BaseDataTable, { TableColumn } from '@/components/Common/BaseDataTable'
import PageHeader from '@/components/Common/PageHeader'
import StatusBadge from '@/components/Common/StatusBadge'
import RoleBadge from '@/components/Common/RoleBadge'
import SendInvitationModal from '@/components/Organization/SendInvitationModal'
import useInvitations from '@/hooks/useInvitations'
import { Invitation, INVITATION_STATUSES } from '@/constants/invitations'

export default function OrganizationInvitationsPage() {
  const { user } = useAuth()
  const { t } = useAppTranslation('common')
  const { invitations, loading, cancelInvitation, refreshInvitations } = useInvitations()

  const { isOpen: isSendOpen, onOpen: onSendOpen, onClose: onSendClose } = useDisclosure()

  const handleSendSuccess = useCallback(() => {
    refreshInvitations()
    onSendClose()
  }, [refreshInvitations, onSendClose])

  const handleCancel = useCallback(
    async (invitationId: number) => {
      try {
        await cancelInvitation(invitationId)
      } catch (error) {
        // Error handling is done in the hook
        console.error('Failed to cancel invitation:', error)
      }
    },
    [cancelInvitation]
  )

  const formatDate = useCallback((dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }, [])

  const isExpired = useCallback((expiresAt: string) => {
    return new Date(expiresAt) < new Date()
  }, [])

  const columns: TableColumn<Invitation>[] = useMemo(
    () => [
      {
        key: 'email',
        header: t('profile.organizationInvitations.columns.email'),
        accessor: (invitation) => (
          <Text fontWeight="medium" color="gray.800">
            {invitation.email}
          </Text>
        ),
      },
      {
        key: 'role',
        header: t('profile.organizationInvitations.columns.role'),
        accessor: (invitation) => <RoleBadge role={invitation.role} />,
      },
      {
        key: 'status',
        header: t('profile.organizationInvitations.columns.status'),
        accessor: (invitation) => <StatusBadge status={invitation.status} />,
      },
      {
        key: 'created_at',
        header: t('profile.organizationInvitations.columns.sent'),
        hideOnMobile: true,
        accessor: (invitation) => (
          <Text fontSize="sm" color="gray.600">
            {formatDate(invitation.created_at)}
          </Text>
        ),
      },
      {
        key: 'expires_at',
        header: t('profile.organizationInvitations.columns.expires'),
        hideOnMobile: true,
        accessor: (invitation) => (
          <Text fontSize="sm" color={isExpired(invitation.expires_at) ? 'red.500' : 'gray.600'}>
            {formatDate(invitation.expires_at)}
          </Text>
        ),
      },
      {
        key: 'actions',
        header: t('profile.organizationInvitations.columns.actions'),
        width: '100px',
        align: 'center' as const,
        accessor: (invitation) => (
          <HStack spacing={1} justify="center">
            {invitation.status === INVITATION_STATUSES.PENDING && (
              <Tooltip label={t('profile.organizationInvitations.cancelInvitation')} placement="top">
                <IconButton
                  aria-label={t('profile.organizationInvitations.cancelInvitation')}
                  icon={<FiTrash2 />}
                  size="sm"
                  variant="ghost"
                  colorScheme="red"
                  onClick={() => handleCancel(invitation.id)}
                  _hover={{ bg: 'red.50' }}
                />
              </Tooltip>
            )}
          </HStack>
        ),
      },
    ],
    [t, formatDate, isExpired, handleCancel]
  )

  return (
    <Box p={{ base: 4, md: 6 }}>
      <PageHeader
        title={t('profile.organizationInvitations.pageTitle')}
        description={t('profile.organizationInvitations.pageSubtitle')}
        actions={[
          {
            label: t('profile.organizationInvitations.sendInvitation'),
            icon: <FiPlus />,
            onClick: onSendOpen,
          },
        ]}
      />

      <BaseDataTable
        columns={columns}
        data={invitations}
        loading={loading}
        emptyMessage={t('profile.organizationInvitations.emptyTitle')}
        emptyDescription={t('profile.organizationInvitations.emptyDescription')}
        emptyIcon={<FiUsers />}
        getRowKey={(invitation) => invitation.id}
        animateRows={true}
        enableHover={true}
      />

      <SendInvitationModal
        isOpen={isSendOpen}
        onClose={onSendClose}
        onSuccess={handleSendSuccess}
      />
    </Box>
  )
}
